import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo } from 'react';

import { SignupAccount, SignupResponse, SignupUKFields } from '@front/pro/src/types/pro';

import useLocalStorage from '../hooks/use-local-storage';
import { useStepper } from '../routes/use-stepper';
import { route } from '../routes/routes';
import { useNavigateHistoryWithQuery } from '../hooks/use-navigate-history-with-query';

type UseRedirectToStepParams = Pick<
  SignupFormContextValue,
  'userInformation' | 'workingInformation' | 'additionalInformation'
> & {
  subscribedPro?: SignupResponse;
};

const useStepCheck = ({ userInformation, workingInformation, subscribedPro }: UseRedirectToStepParams) => {
  const { currentStep } = useStepper();

  if (!subscribedPro && currentStep === 'confirmation') {
    return route('root');
  }

  if (!userInformation && ['additional_information', 'pro_status'].includes(currentStep)) {
    return route('root');
  }
  if (!workingInformation && currentStep === 'additional_information') {
    return route('pro_status');
  }

  return undefined;
};

export type WorkingInformationFR = Pick<
  SignupAccount,
  | 'company_status'
  | 'residence_permit_expiration_date'
  | 'residence_permit_type'
  | 'can_work_in_france'
  | 'siren'
  | 'nova_status'
>;

export type WorkingInformationUK = Pick<SignupAccount, 'siren'> & SignupUKFields;

export type UserInformation = Pick<
  SignupAccount,
  | 'salutation'
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'password'
  | 'mobile'
  | 'address'
  | 'date_of_birth'
  | 'street_number'
  | 'street_name'
  | 'city'
  | 'zip_code'
  | 'country_code'
  | 'lat'
  | 'lng'
> & {
  phoneFlag?: string;
};

export type AdditionalInformation = Pick<
  SignupAccount,
  | 'weekly_hours'
  | 'also_a_nanny'
  | 'internet_promotion_authorisation'
  | 'declarative_source'
  | 'childcare_availability'
>;

export type RevenueSimulatorInformation = { hours_simulator: string };

type SignupFormContextValue = {
  revenueSimulator?: RevenueSimulatorInformation;
  userInformation?: UserInformation;
  workingInformation?: WorkingInformationFR | WorkingInformationUK;
  additionalInformation?: AdditionalInformation;
  subscribedPro?: SignupResponse;
  setRevenueSimulator?: (values: RevenueSimulatorInformation) => void;
  setUserInformation?: (values: UserInformation) => void;
  setWorkingInformation?: (values: WorkingInformationFR | WorkingInformationUK) => void;
  setAdditionalInformation?: (values: AdditionalInformation) => void;
  setSubscribedPro?: (values: SignupResponse) => void;
};

const SignupFormContext = createContext<SignupFormContextValue>({});

export const SignupFormContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const { historyReplaceWithQuery } = useNavigateHistoryWithQuery();
  const [revenueSimulator, setRevenueSimulator] = useLocalStorage<RevenueSimulatorInformation>('revenueSimulator');
  const [userInformation, setUserInformation] = useLocalStorage<UserInformation>('userInformation');
  const [workingInformation, setWorkingInformation] = useLocalStorage<WorkingInformationFR | WorkingInformationUK>(
    'workingInformation',
  );
  const [additionalInformation, setAdditionalInformation] =
    useLocalStorage<AdditionalInformation>('additionalInformation');
  const [subscribedPro, setSubscribedPro] = useLocalStorage<SignupResponse | undefined>('subscribedPro');
  const stepToRedirectTo = useStepCheck({ userInformation, workingInformation, subscribedPro });

  useEffect(() => {
    if (stepToRedirectTo) historyReplaceWithQuery(stepToRedirectTo);
  }, [historyReplaceWithQuery, stepToRedirectTo]);

  const value = useMemo(
    () => ({
      revenueSimulator,
      userInformation,
      workingInformation,
      additionalInformation,
      subscribedPro,
      setUserInformation,
      setWorkingInformation,
      setAdditionalInformation,
      setRevenueSimulator,
      setSubscribedPro,
    }),
    [
      revenueSimulator,
      userInformation,
      workingInformation,
      additionalInformation,
      subscribedPro,
      setUserInformation,
      setWorkingInformation,
      setAdditionalInformation,
      setRevenueSimulator,
      setSubscribedPro,
    ],
  );

  return <SignupFormContext.Provider value={value}>{children}</SignupFormContext.Provider>;
};

export const useAdditionalInformation = () => {
  const { additionalInformation, setAdditionalInformation } = useContext(SignupFormContext);
  if (!setAdditionalInformation) {
    throw new Error('Use this hook within the SignupFormContext provider');
  }
  return { additionalInformation, setAdditionalInformation };
};

export const useWorkingInformation = () => {
  const { workingInformation, setWorkingInformation } = useContext(SignupFormContext);
  if (!setWorkingInformation) {
    throw new Error('Use this hook within the SignupFormContext provider');
  }
  return { workingInformation, setWorkingInformation };
};

export const useUserInformation = () => {
  const { userInformation, setUserInformation } = useContext(SignupFormContext);
  if (!setUserInformation) {
    throw new Error('Use this hook within the SignupFormContext provider');
  }
  return { userInformation, setUserInformation };
};

export const useSubscribedPro = () => {
  const { subscribedPro, setSubscribedPro } = useContext(SignupFormContext);
  if (!setSubscribedPro) {
    throw new Error('Use this hook within the SignupFormContext provider');
  }
  return { subscribedPro, setSubscribedPro };
};

export const useRevenueSimulatorInformation = () => {
  const { revenueSimulator, setRevenueSimulator } = useContext(SignupFormContext);
  if (!setRevenueSimulator) {
    throw new Error('Use this hook within the SignupFormContext provider');
  }
  return { revenueSimulator, setRevenueSimulator };
};
